import React from 'react';
import { Checkr, Certn } from '@/assets/icons/ats';
import { Flex, Icon, Button } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { NotificationCard } from '../../components/Card';
import { decode } from 'js-base64';
import { NotificationTime } from '../../components/Time';

interface NotificationData {
  actions: string;
  archived_at: string | null;
  category: string;
  content: string;
  created_at: string;
  custom_attributes: string;
  id: string;
  meta: any | null;
  notification_type: number;
  read_at: string | null;
  recipient: number;
  recipient_receiver: string;
  seen_at: string | null;
  sent_at: string | null;
  title: string;
  topic: string;
  type: number;
  updated_at: string;
  user_id: string;
}

interface DecodedCustomAttributes {
  url: string;
}

interface NotificationWrapper {
  notification: NotificationData;
  components: {
    OfferApprovalView: () => void;
  };
  setMarkAll: () => void;
  onClose: () => void;
}

// Generic mapping - Add new providers here
const IntegrationNotificationMap = {
  CHECKR: {
    CONNECTED: 86,
    DISCONNECTED: 87,
  },
  CERTN: {
    CONNECTED: 103,
    DISCONNECTED: 104,
  },
} as const;

type ProviderKey = keyof typeof IntegrationNotificationMap;

// Provider configuration - Add new providers here with their icon and name
const ProvidersConfig: Record<
  ProviderKey,
  { icon: React.ComponentType<any>; name: string }
> = {
  CHECKR: {
    icon: Checkr,
    name: 'Checkr',
  },
  CERTN: {
    icon: Certn,
    name: 'Certn',
  },
} as const;

const detectProvider = (type: number): ProviderKey => {
  for (const [providerKey, notificationTypes] of Object.entries(
    IntegrationNotificationMap
  )) {
    if (
      type === notificationTypes.CONNECTED ||
      type === notificationTypes.DISCONNECTED
    ) {
      return providerKey as ProviderKey;
    }
  }
  // Fallback to CHECKR for backward compatibility
  return 'CHECKR';
};

export const BackgroundCheckNotification = (
  notification: NotificationWrapper = {} as NotificationWrapper
) => {
  const { custom_attributes, notification_type } =
    notification?.notification || {};

  let data: DecodedCustomAttributes | null = null;
  if (custom_attributes) {
    try {
      data = JSON.parse(decode(custom_attributes));
    } catch (err) {
      console.error('Error decoding custom_attributes', err);
    }
  }

  const providerKey = detectProvider(notification_type);
  const provider = ProvidersConfig[providerKey];
  const notificationTypes = IntegrationNotificationMap[providerKey];
  const isConnected = notification_type === notificationTypes.CONNECTED;

  const title = isConnected
    ? `Your ${provider.name} account has been connected!`
    : `Your ${provider.name} account has been disconnected!`;

  return (
    <NotificationCard notification={notification}>
      <Flex alignItems="start" gap="14px">
        <Icon as={provider.icon as any} w="24px" h="24px" />

        <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start">
          <KText>{title}</KText>
          <NotificationTime notification={notification?.notification} />
          <Button
            w="fit-content"
            variant="primary"
            onClick={() => {
              window.open(data?.url, '_target');
            }}
          >
            Go to settings{' '}
          </Button>
        </Flex>
      </Flex>
    </NotificationCard>
  );
};
